import {
    Zap, BatteryCharging, PlugZap, Gauge, Cpu, Factory,
    Waypoints, Lightbulb, Car, Smartphone, RadioTower, CircuitBoard, ArrowRight,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import { Eyebrow, FadeUp } from "@/components/Reveal";
import { useQuote } from "@/components/QuoteProvider";
import { SEGMENTS } from "@/data/site";

const ICONS = {
    Zap, BatteryCharging, PlugZap, Gauge, Cpu, Factory,
    Waypoints, Lightbulb, Car, Smartphone, RadioTower, CircuitBoard,
};

export default function MarketSegments() {
    const { openQuote } = useQuote();
    return (
        <>
            <PageHero
                crumb="Market Segments"
                eyebrow="Applications We Manufacture For"
                titleLines={["Market", "Segments."]}
                lead="PCBs manufactured for electronics manufacturers and industrial customers across twelve key market segments."
            />

            <section className="bg-white py-20 md:py-28" data-testid="segments-page">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <FadeUp className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
                        <div>
                            <Eyebrow>12 Market Segments</Eyebrow>
                            <h2 className="mt-5 max-w-2xl text-3xl font-bold tracking-tight text-navy md:text-4xl">
                                PCBs for every application we serve
                            </h2>
                        </div>
                        <p className="max-w-md text-[14px] leading-relaxed text-slate-500">
                            Each segment places different demands on a board — current carrying,
                            thermal management, density or durability. We manufacture to match.
                        </p>
                    </FadeUp>

                    <div className="mt-14 grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3" data-testid="segments-grid">
                        {SEGMENTS.map((s, i) => {
                            const Icon = ICONS[s.icon];
                            return (
                                <FadeUp
                                    key={s.id}
                                    delay={(i % 3) * 0.07}
                                    className="group scroll-mt-28 bg-white"
                                >
                                    <div id={s.id} className="h-full scroll-mt-28 p-7 transition-colors duration-200 group-hover:bg-navy" data-testid={`segment-card-${s.id}`}>
                                        <div className="flex items-start justify-between">
                                            <Icon className="h-7 w-7 text-brand-red" strokeWidth={1.5} />
                                            <span className="font-mono text-[11px] tracking-[0.2em] text-slate-300 transition-colors group-hover:text-slate-500">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                        </div>
                                        <h3 className="mt-6 text-lg font-semibold text-navy transition-colors group-hover:text-white">
                                            {s.name}
                                        </h3>
                                        <p className="mt-3 text-[13.5px] leading-relaxed text-slate-500 transition-colors group-hover:text-slate-300">
                                            {s.desc}
                                        </p>
                                    </div>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="border-t border-slate-200 bg-slate-50 py-16 md:py-20" data-testid="segments-cta">
                <div className="mx-auto flex max-w-7xl flex-col justify-between gap-8 px-4 sm:px-6 lg:flex-row lg:items-center lg:px-8">
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight text-navy md:text-3xl">
                            Your segment not listed? Talk to us.
                        </h2>
                        <p className="mt-3 max-w-xl text-[14px] leading-relaxed text-slate-500">
                            Share your PCB requirement and application — we will confirm how we can
                            support it.
                        </p>
                    </div>
                    <button
                        onClick={openQuote}
                        data-testid="segments-cta-btn"
                        className="inline-flex shrink-0 items-center gap-2 border border-brand-red bg-brand-red px-8 py-4 text-[13px] font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-redhover"
                    >
                        Get a Quote <ArrowRight className="h-4 w-4" />
                    </button>
                </div>
            </section>
        </>
    );
}
